import { injectable } from 'inversify';
import { DiContainer } from '@@/di/di-container';
import { ClientStorageService } from '@@/common/services/client-storage.service';
import { AnalyticsMixingData } from '@@/mixing/ts/analytic.types';
import { get, post } from '@api/api-client';
import {
  IMixingPrograms,
  IRecipeMixingService,
  ISearchBatchesRequest,
} from './IRecipeMixingService.interface';
import { IMixRoot, IWarehousesList } from '../ts';
import { IWarehouseApiWithBags } from '@/modules/warehouses/ts';
import { ISearchFilters } from '@/modules/search/store/search.store';
import { multiWheatType } from '@/modules/common/helpers/wheat-type.helper';

@injectable()
export class RecipeMixingService implements IRecipeMixingService {
  constructor(
    private readonly clientStorageService = DiContainer.get(ClientStorageService),
  ) {}

  getWarehousesForMixing(args: {
    tons?: number;
    recipesCount?: number;
    mixAlgo?: number;
  }): Promise<IWarehousesList> {
    return post(
      `${PUBLIC_API_URL}/mixingrecipe/warehouses`,
      {
        num_recipes:
          args.recipesCount && args.recipesCount > 0 ? args.recipesCount : undefined,
        max_weight: args.tons && args.tons < 0 ? undefined : args.tons,
        mix_algo: args.mixAlgo && args.mixAlgo > 0 ? args.mixAlgo : undefined,
      },
      document.cookie,
    );
  }

  getMyWarehouses() {
    return get(`${PUBLIC_API_URL}/warehouse`, {}, document.cookie);
  }

  getWarehouseForCalculator({
    id,
    limit,
    page,
  }: {
    id: number;
    limit?: number;
    page?: number;
  }) {
    return get(
      `${PUBLIC_API_URL}/warehouse_calc/${id}`,
      {
        limit: 10000,
        page: 0,
      },
      document.cookie,
    );
  }

  getBuyersPrices(filters: ISearchFilters) {
    const classes = filters.wheatClass.filter((item) => item.checked);
    const buyers = filters.buyers.filter((item) => item.enabled)?.[0].id;
    const sort = filters.sort.filter((item) => item.enabled);
    const type = filters.type.filter((item) => item.enabled);

    let resType: number[] = [];

    /* 
      тут идет проверка на сорт и тип. Если выбран один параметр из двух, я подставляю типы вручную.
      Дело в том, что типаж пшеницы формируется из значений этих двух параметров.
    */
    const activeTypesCount = filters.type.filter((item) => item.enabled).length;
    const activeSortsCount = filters.sort.filter((item) => item.enabled).length;

    if (activeTypesCount === 0) {
      if (
        filters.sort.find((item) => item.value === 'твердая' && item.enabled)?.value
      ) {
        resType = [...resType, 2, 6];
      }
      if (
        filters.sort.find((item) => item.value === 'мягкая белозерная' && item.enabled)
          ?.value
      ) {
        resType = [...resType, 3, 5];
      }
      if (
        filters.sort.find(
          (item) => item.value === 'мягкая краснозерная' && item.enabled,
        )?.value
      ) {
        resType = [...resType, 1, 4];
      }

      resType = [...new Set(resType)];
    }

    if (activeSortsCount === 0) {
      if (filters.type.find((item) => item.value === 'яровая' && item.enabled)?.value) {
        resType = [...resType, 1, 2, 3];
      }
      if (filters.type.find((item) => item.value === 'озимая' && item.enabled)?.value) {
        resType = [...resType, 4, 5, 6];
      }
      resType = [...new Set(resType)];
    }

    const body: { class: string; active?: boolean; type: string } = {
      class: JSON.stringify(classes.map((item) => +item.id)),
      active: buyers === 1 ? true : false,
      type:
        resType.length > 0
          ? JSON.stringify(resType)
          : JSON.stringify(
              multiWheatType(
                sort.map((item) => item.value),
                type.map((item) => item.value),
              ),
            ),
    };

    if (buyers === 0) delete body.active;
    return get(`${PUBLIC_API_URL}/prices_buyers`, body, document.cookie);
  }

  getTopRecipes(warehouse_id: number): Promise<any> {
    return post(
      `${PUBLIC_API_URL}/mixingrecipe/top`,
      {
        warehous_id: warehouse_id,
      },
      document.cookie,
    );
  }

  getAvailableBunkers() {
    return get(`${PUBLIC_API_URL}/bunker`, {}, document.cookie);
  }

  fetchRecipeData(recipeId: number, bunkers: any) {
    return post(
      `${PUBLIC_API_URL}/mixingrecipe`,
      {
        top_id: recipeId,
        bunker_model_id: bunkers,
      },
      document.cookie,
    );
  }

  getSearchBatches(params: ISearchBatchesRequest) {
    return post(`${PUBLIC_API_URL}/search`, { ...params });
  }

  getWarehouseById(id: number): Promise<IWarehouseApiWithBags> {
    return get(`${PUBLIC_API_URL}/warehouse/${id}`, {}, document.cookie);
  }

  getWarehouseDetailsById(id: number) {
    return get(`${PUBLIC_API_URL}/more_cost/${id}`, {});
  }

  getMixingParams(body: IMixingPrograms) {
    return post(`${PUBLIC_API_URL}/mixingparams`, { ...body }, document.cookie);
  }

  getRecipeMix(id: number): Promise<IMixRoot> {
    return post(`${PUBLIC_API_URL}/recipe_mix`, { top_id: id }, document.cookie);
  }
}
